/**
 * 回拨电话模块
 *
 * 手机验证统一走 phone-verify 组件（C1-C3）：分步 + 6 格 OTP + 统一国家选择器。
 * 本模块只负责：注入「邮箱/留言」可选字段 + 校验通过后提交线索 + 成功页。
 */

import { setPanelContent, getShadowRoot } from "../../ui/container";
import { t } from "../../ui/i18n";
import { collectVisitorInfo } from "../../core/visitor-info";
import { phoneVerifyHtml, bindPhoneVerify, type PhoneVerifyOptions, type PvCtx } from "../phone-verify/render";
import { resolveApiBase } from "../../core/origin";
import type { Locale } from "../../types";

// 机器人图标（与语音对话窗口标题一致）
function svgBotCb(): string {
  return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="8" width="16" height="11" rx="3"/><path d="M12 8V4"/><circle cx="12" cy="3" r="1.2" fill="currentColor"/><circle cx="9" cy="13" r="1.3" fill="currentColor" stroke="none"/><circle cx="15" cy="13" r="1.3" fill="currentColor" stroke="none"/></svg>`;
}

function getColor(): string {
  try {
    const r = getShadowRoot();
    if (r) { const v = getComputedStyle(r.host).getPropertyValue("--oc-primary").trim(); if (v) return v; }
  } catch (e) { /* ignore */ }
  return "#533afd";
}
function getApiBase(): string {
  // 跨站嵌入：用 SDK 源站，绝不回退 location.origin（宿主站会 404）
  return resolveApiBase();
}

/* ────── 主渲染 ────── */
export function renderCallbackPanel(locale: Locale, widgetId?: string): void {
  const c = getColor();

  // 邮箱/留言折叠为「可选」（I1），作为额外字段注入手机号步骤
  const extraFieldsHtml = /* html */`
    <button type="button" class="oc-cb-optional-toggle" id="oc-cb-optional-toggle" aria-expanded="false">
      <span>${t("callback.optionalToggle", locale)}</span><span class="oc-cb-optional-caret">▾</span>
    </button>
    <div id="oc-cb-optional" style="display:none;">
      <input type="email" id="oc-cb-email" class="oc-cb-input" placeholder="${t("callback.emailPlaceholder", locale)}" autocomplete="email" />
      <textarea id="oc-cb-message" class="oc-cb-textarea" rows="2" placeholder="${t("callback.messagePlaceholder", locale)}"></textarea>
    </div>`;

  const pvOpts: PhoneVerifyOptions = {
    locale,
    primaryColor: c,
    submitLabel: t("callback.confirm", locale),
    privacy: false,
    extraFieldsHtml,
    onVerified: (ctx) => submitCallback(ctx, locale, widgetId || ""),
  };

  const html = /* html */`
<div class="oc-callback oc-cb-root">
  <div class="oc-cb-header">${svgBotCb()} <span>${t("callback.title", locale)}</span></div>
  <div id="oc-cb-pv-wrap">
    ${phoneVerifyHtml(pvOpts)}
    <p class="oc-cb-hint">${t("callback.hint", locale)}</p>
  </div>

  <!-- 成功 -->
  <div id="oc-cb-step-success" style="display:none;text-align:center;padding:24px 0;">
    <div style="display:flex;justify-content:center;margin-bottom:16px;color:#16a34a;"><svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg></div>
    <p style="font-size:16px;font-weight:600;color:var(--oc-text);margin-bottom:8px;">${t("callback.success", locale)}</p>
    <p style="font-size:13px;color:var(--oc-text-secondary);">${t("callback.willCall", locale)} <strong id="oc-cb-success-phone"></strong></p>
    <p style="font-size:12px;color:var(--oc-text-secondary);margin-top:10px;line-height:1.6;">${t("callback.successExpect", locale)}</p>
  </div>

  <style>
    .oc-cb-root { min-height:100%;box-sizing:border-box;padding:16px;display:flex;flex-direction:column;justify-content:center; }
    .oc-cb-header { font-size:16px;font-weight:600;color:var(--oc-text);text-align:center;margin-bottom:14px;padding:12px 0;display:flex;align-items:center;justify-content:center;gap:7px; }
    .oc-cb-header svg { width:18px;height:18px; }
    .oc-cb-input { width:100%;padding:10px 12px;border:1px solid var(--oc-border);border-radius:8px;font-size:14px;margin-bottom:12px;background:var(--oc-bg);color:var(--oc-text);box-sizing:border-box; }
    .oc-cb-textarea { width:100%;padding:10px 12px;border:1px solid var(--oc-border);border-radius:8px;font-size:14px;resize:none;margin-bottom:8px;background:var(--oc-bg);color:var(--oc-text);box-sizing:border-box; }
    .oc-cb-optional-toggle { display:flex;align-items:center;justify-content:space-between;width:100%;border:none;background:none;cursor:pointer;padding:4px 2px 12px;font-size:13px;color:${c}; }
    .oc-cb-optional-caret { transition:transform 0.15s;font-size:11px; }
    .oc-cb-hint { text-align:center;font-size:12px;margin-top:16px;color:var(--oc-text-secondary); }
  </style>
</div>`;

  setPanelContent(html);
  bindPhoneVerify(pvOpts);

  // 可选信息折叠开关（I1）
  const root = getShadowRoot();
  const optionalToggle = root?.getElementById("oc-cb-optional-toggle");
  const optionalBox = root?.getElementById("oc-cb-optional");
  optionalToggle?.addEventListener("click", () => {
    if (!optionalBox) return;
    const open = optionalBox.style.display !== "none";
    optionalBox.style.display = open ? "none" : "block";
    optionalToggle.setAttribute("aria-expanded", String(!open));
    const caret = optionalToggle.querySelector(".oc-cb-optional-caret") as HTMLElement;
    if (caret) caret.style.transform = open ? "rotate(0deg)" : "rotate(180deg)";
  });
}

/* ────── 校验通过 → 提交线索 → 成功页 ────── */
async function submitCallback(ctx: PvCtx, locale: Locale, widgetId: string): Promise<void> {
  const root = ctx.root;
  const email = (root.getElementById("oc-cb-email") as HTMLInputElement)?.value?.trim() || "";
  const message = (root.getElementById("oc-cb-message") as HTMLTextAreaElement)?.value?.trim() || "";
  const info = collectVisitorInfo();

  try {
    const res = await fetch(`${getApiBase()}/widget/lead`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        widgetId,
        name: "",
        phone: ctx.phone,
        email,
        countryCode: ctx.dialCode,
        pageUrl: info.pageUrl,
        utmParams: info.utm,
        device: info.deviceJson,
        browser: info.browserJson,
        actionType: "callback",
        actionLabel: "回拨请求",
        callbackMessage: message,
        metadata: { visitorId: info.visitorId, countryCode: ctx.country.code, referrer: info.referrer },
      }),
    });
    if (!res.ok) {
      let serverMsg = ""; try { const b = await res.json(); serverMsg = b?.error || ""; } catch { /* ignore */ }
      throw new Error(serverMsg || `${t("callback.submitFail", locale)} (${res.status})`);
    }
    // 成功：隐藏验证区，显示成功页
    const wrap = root.getElementById("oc-cb-pv-wrap"); if (wrap) wrap.style.display = "none";
    const succ = root.getElementById("oc-cb-step-success"); if (succ) succ.style.display = "block";
    const ph = root.getElementById("oc-cb-success-phone"); if (ph) ph.textContent = ctx.fullPhone;
  } catch (e: any) {
    ctx.setError(e?.message || t("callback.submitFailRetry", locale));
    ctx.setBusy(false);
  }
}
